import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { AppBaseDropdown, AppButton, AppSingleSelect, AppTag } from '@pro-sto/ui';
import type { SelectOption } from '@pro-sto/ui';
import { DotsThreeVerticalIcon, ExclamationMarkIcon } from '@/components/icons';
import { operatingHoursStore, shiftViewStore } from '@/stores';
import type { ShiftMember } from '@/types/shift.types';
import {
  MEMBER_STATUS_LABEL,
  SHIFT_VIEW_STATUS_LABEL,
  accrualRows,
  accrualTitle,
  employeeCountLabel,
  formatAccrualAmount,
  headerIntervalLabel,
  memberClockValue,
  memberInitials,
  memberIntervalLabel,
  memberMenu,
  memberTagColor,
  savesPlannedMemberTime,
  shiftTagColor,
  type MemberMenuAction,
} from './shiftDayModel';
import ShiftTimeModal from './ShiftTimeModal';

interface ShiftRosterProps {
  uuid: string;
}

const ShiftRoster: React.FC<ShiftRosterProps> = observer(({ uuid }) => {
  const [menuMemberUuid, setMenuMemberUuid] = useState<string | null>(null);
  const detail = shiftViewStore.detail;
  const timeZone = operatingHoursStore.timezone || 'Europe/Moscow';

  useEffect(() => {
    shiftViewStore.load(uuid);
  }, [uuid]);

  useEffect(() => {
    setMenuMemberUuid(null);
  }, [uuid]);

  if (shiftViewStore.loadError && detail?.uuid !== uuid) {
    return (
      <div className="shift-day__error" data-testid="shifts-day-error">
        <ExclamationMarkIcon size={24} />
        <p className="shift-day__error-message">{shiftViewStore.loadError}</p>
        <AppButton
          variant="secondary"
          onClick={() => shiftViewStore.load(uuid)}
          data-testid="shifts-day-retry"
        >
          Повторить
        </AppButton>
      </div>
    );
  }

  if (!detail || detail.uuid !== uuid) {
    return (
      <div className="shift-day__loading" data-testid="shifts-day-loading">
        <span className="shift-day__skeleton shift-day__skeleton_title" />
        <span className="shift-day__skeleton shift-day__skeleton_text" />
        <span className="shift-day__skeleton shift-day__skeleton_actions" />
      </div>
    );
  }

  const closed = detail.status === 'closed';
  const responsibleValue =
    shiftViewStore.responsiblePending !== undefined
      ? shiftViewStore.responsiblePending
      : detail.responsible_employee_uuid;
  const responsibleOptions = responsibleSelectOptions(detail.members, detail.responsible);
  const responsibleOption =
    responsibleOptions.find((option) => option.value === responsibleValue) ?? null;
  const title = accrualTitle(detail.status);
  const rows = title ? accrualRows(detail.accrual) : [];

  const runAction = (member: ShiftMember, action: MemberMenuAction) => {
    setMenuMemberUuid(null);
    if (action === 'change_time') {
      const planned = savesPlannedMemberTime(detail.status, member.status);
      shiftViewStore.openTimeModal(
        member.uuid,
        memberClockValue(planned ? null : member.actual_start, member.planned_start, timeZone),
        memberClockValue(planned ? null : member.actual_end, member.planned_end, timeZone),
      );
      return;
    }
    void shiftViewStore.runMemberAction(member.uuid, action);
  };

  return (
    <div className="shift-day shift-day_roster" data-testid="shifts-day-roster">
      {shiftViewStore.actionError ? (
        <p className="shift-roster__banner" data-testid="shift-action-error">
          {shiftViewStore.actionError}
        </p>
      ) : null}
      {shiftViewStore.loadError ? (
        <div className="shift-roster__banner" data-testid="shift-refresh-error">
          <p>{shiftViewStore.loadError}</p>
          <AppButton
            variant="secondary"
            onClick={() => shiftViewStore.load(uuid)}
            data-testid="shift-refresh-retry"
          >
            Повторить
          </AppButton>
        </div>
      ) : null}
      <div className="shift-roster">
        <section className="shift-roster__card" aria-labelledby="shift-roster-title">
          <h2 className="shift-roster__title" id="shift-roster-title">
            Состав смены
          </h2>
          <div className="shift-roster__columns" aria-hidden="true">
            <span>Сотрудник</span>
            <span>Время работы</span>
            <span>Роль</span>
            <span>Статус</span>
          </div>
          {detail.members.length === 0 ? (
            <p className="shift-roster__empty" data-testid="shift-roster-empty">
              В смене пока нет сотрудников
            </p>
          ) : (
            <ul className="shift-roster__list">
              {detail.members.map((member) => {
                const interval = memberIntervalLabel(member, timeZone);
                const menuItems = memberMenu(member.status, detail.status);
                const menuDisabled = shiftViewStore.isBusy || menuItems.length === 0;
                return (
                  <li
                    className="shift-roster__row"
                    key={member.uuid}
                    data-testid={`shift-member-${member.uuid}`}
                  >
                    <div className="shift-roster__person">
                      <span className="shift-roster__avatar" aria-hidden="true">
                        {memberInitials(member.name)}
                      </span>
                      <span className="shift-roster__name" title={member.name}>
                        {member.name}
                      </span>
                    </div>
                    <span className="shift-roster__time" title={interval}>
                      {interval || '—'}
                    </span>
                    <span className="shift-roster__role" title={member.position ?? undefined}>
                      {member.position || '—'}
                    </span>
                    <div className="shift-roster__status">
                      <AppTag
                        size="M"
                        color={memberTagColor(member.status)}
                        data-testid={`shift-member-status-${member.uuid}`}
                      >
                        {MEMBER_STATUS_LABEL[member.status]}
                      </AppTag>
                    </div>
                    <AppBaseDropdown
                      opened={menuMemberUuid === member.uuid}
                      onClose={() => setMenuMemberUuid(null)}
                      dropdownWidth="240px"
                      xDirection="right"
                      yDirection="bottom"
                      toggle={
                        <button
                          type="button"
                          className="shift-roster__menu-button"
                          aria-label={`Действия: ${member.name}`}
                          disabled={menuDisabled}
                          onClick={() =>
                            setMenuMemberUuid((current) =>
                              current === member.uuid ? null : member.uuid,
                            )
                          }
                          data-testid={`shift-member-menu-${member.uuid}`}
                        >
                          <DotsThreeVerticalIcon size={20} />
                        </button>
                      }
                      dropdown={
                        <div className="shift-roster__menu" role="menu">
                          {menuItems.map((item) => (
                            <button
                              key={item.action}
                              type="button"
                              role="menuitem"
                              className="shift-roster__menu-item"
                              onClick={() => runAction(member, item.action)}
                              data-testid={`shift-member-action-${member.uuid}-${item.action}`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <aside className="shift-roster__aside">
          <section className="shift-roster__card shift-roster__card_details">
            <h2 className="shift-roster__title">Детали смены</h2>
            <div className="shift-roster__status-line">
              <AppTag
                size="M"
                color={shiftTagColor(detail.status)}
                data-testid="shift-detail-status"
              >
                {SHIFT_VIEW_STATUS_LABEL[detail.status]}
              </AppTag>
              <span className="shift-roster__interval" data-testid="shift-detail-interval">
                {headerIntervalLabel(detail.header, timeZone)}
              </span>
            </div>
            <AppSingleSelect
              label="Ответственный"
              placeholder="Не выбран"
              options={responsibleOptions}
              value={responsibleOption}
              background="muted"
              disabled={
                closed ||
                shiftViewStore.isBusy ||
                responsibleOptions.length === 0
              }
              onChange={(option) => {
                if (closed) return;
                void shiftViewStore.setResponsible((option?.value as string | null) ?? null);
              }}
              data-testid="shift-responsible"
            />
            {shiftViewStore.closeViolations.length > 0 ? (
              <ul className="shift-roster__violations" data-testid="shift-close-violations">
                {shiftViewStore.closeViolations.map((message, index) => (
                  <li key={`${index}-${message}`}>{message}</li>
                ))}
              </ul>
            ) : null}
          </section>

          {title ? (
            <section className="shift-roster__accrual" data-testid="shift-accrual">
              <h2 className="shift-roster__title" data-testid="shift-accrual-title">
                {title}
              </h2>
              <div className="shift-roster__total">
                <span data-testid="shift-accrual-total">
                  {formatAccrualAmount(detail.accrual.total)}
                </span>
                <span className="shift-roster__count" data-testid="shift-accrual-count">
                  {employeeCountLabel(detail.accrual.employee_count)}
                </span>
              </div>
              <ul className="shift-roster__accrual-rows">
                {rows.map((row) => (
                  <li key={row.kind} data-testid={`shift-accrual-${row.kind}`}>
                    <span>{row.label}</span>
                    <span>{formatAccrualAmount(row.amount)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>
      <ShiftTimeModal />
    </div>
  );
});

const responsibleSelectOptions = (
  members: ShiftMember[],
  responsible: { uuid: string; name: string } | null,
): SelectOption[] => {
  const options: SelectOption[] = [];
  const seen = new Set<string>();
  for (const member of members) {
    if (seen.has(member.employee_uuid)) continue;
    seen.add(member.employee_uuid);
    options.push({ label: member.name, value: member.employee_uuid });
  }
  if (responsible && !seen.has(responsible.uuid)) {
    options.unshift({
      label: responsible.name || 'Ответственный',
      value: responsible.uuid,
    });
  }
  return options;
};

ShiftRoster.displayName = 'ShiftRoster';

export default ShiftRoster;
